import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Menu, MenuItem, Drawer, Box, Tooltip } from '@mui/material';
import { hasPermission } from '../../context/AuthFunc';
import { baseURL, PERMISSIONS } from '../../constants/const';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';
import DownloadApp from '../DownloadAPP/DownloadAPP';
import IconButton from '@mui/material/IconButton';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Menu as MenuIcon, InfoRounded } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import MonitorIcon from '@mui/icons-material/Monitor';
import UserManuals from '../UserManuals/UserManuals';
import AccountMenu from '../SettingsMenu/SettingsMenu';
import DesktopMenu from '../DesktopMenu/DesktopMenu.jsx';
import { useLocation } from 'react-router-dom';
import { getMode, getCameraMode, createBackup, latestBackup, getUpdate, runUpdate } from '../../api/axios.js'


const Navbar = ({ isLoggedIn, setIsLoggedIn }) => {
  const { t } = useTranslation('global');

  const [adminAnchorEl, setAdminAnchorEl] = useState(null);
  const [salesAnchorEl, setSalesAnchorEl] = useState(null);
  const [lockersAnchorEl, setLockersAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("0");
  const [cameraMode, setCameraMode] = useState("0");
  const [backupNeeded, setBackupNeeded] = useState("0");
  const [updateNeeded, setUpdateNeeded] = useState("0");
  const [isLoadingBackup, setIsLoadingBackup] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [updateFileName, setUpdateFileName] = useState("");

  const location = useLocation();

  const LockersMenu = {
    name: t("navbar.lockers"),
    subMenu: [
      {
        name: t("navbar.lockersArangment"),
        link: "/locker-arangment",
        permission: PERMISSIONS.NEW_EMPLOYEE
      },
      {
        name: t("navbar.viewLockers"),
        link: "/lockers",
        permission: PERMISSIONS.NEW_EMPLOYEE
      },
      {
        name: t("navbar.lockersMonitoring"),
        link: "/lockers-monitoring",
        permission: PERMISSIONS.NEW_EMPLOYEE
      }

    ]
  }

  const CanteenMenu = {
    name: t("navbar.canteen"),
    subMenu: [
      {
        name: t("navbar.newEmployee"),
        link: "/new-employee",
        permission: PERMISSIONS.NEW_EMPLOYEE
      },
      {
        name: t("navbar.newOrganizationUnit"),
        link: "/new-organization-unit",
        permission: PERMISSIONS.ORGANIZATION_UNITS
      },
      {
        name: t("navbar.newArticle"),
        link: "/new-article",
        permission: PERMISSIONS.ARTICLE
      },
      {
        name: t("navbar.newUnitOfMeasure"),
        link: "/new-unit-of-measure",
        permission: PERMISSIONS.UNIT_OF_MEASURE
      },
      {
        name: t("navbar.newIngredient"),
        link: "/new-ingredient",
        permission: PERMISSIONS.INGREDIENTS
      }
    ]
  }

  const FiscalizationMenu = {
    name: t("navbar.fiscalization"),
    subMenu: [
      {
        name: t("navbar.refund"),
        link: "/refund",
        permission: PERMISSIONS.REFUND
      },
      {
        name: t("navbar.reports"),
        link: "/fiscalization-reports",
        permission: PERMISSIONS.FISCALIZATION_REPORTS
      }
    ]
  }

  const SalesMenu = {
    name: t("navbar.sales"),
    subMenu: [
      {
        name: t("navbar.newPlasticTicket"),
        link: "/new-ticket",
        permission: PERMISSIONS.CREATE_NEW_TICKET
      },
      {
        name: t("navbar.newPaperTicket"),
        link: "/new-paper-ticket",
        permission: PERMISSIONS.CREATE_NEW_TICKET
      },
      {
        name: t("navbar.addItem"),
        link: "/new-item",
        permission: PERMISSIONS.CREATE_NEW_ITEM
      },
      {
        name: t("navbar.edit/deleteItem"),
        link: "/price-list",
        permission: PERMISSIONS.VIEW_PRICELIST
      },
      {
        name: t("navbar.addKind"),
        link: "/add-kind",
        permission: PERMISSIONS.CREATE_NEW_KIND
      },
      {
        name: t("navbar.addCategory"),
        link: "/add-category",
        permission: PERMISSIONS.CREATE_NEW_CATEGORY
      },
      {
        name: t("navbar.addType"),
        link: "/add-type",
        permission: PERMISSIONS.CREATE_NEW_TYPE
      },
      {
        name: t("navbar.purchasing"),
        link: "/purchasing",
        permission: PERMISSIONS.VIEW_PURCHASING
      }
    ]
  }
  const AdminMenu = {
    name: t("navbar.admin"),
    subMenu: [
      {
        name: t("navbar.ticketDesign"),
        link: "/ticket-design",
        permission: PERMISSIONS.TICKET_DESIGN
      },
      {
        name: t("navbar.addReader"),
        link: "/new-reader",
        permission: PERMISSIONS.CREATE_NEW_READER
      },
      {
        name: t("navbar.edit/deleteReader"),
        link: "/readers",
        permission: PERMISSIONS.EDIT_READER
      },
      {
        name: t("navbar.addUser"),
        link: "/new-user",
        permission: PERMISSIONS.CREATE_NEW_USER
      },
      {
        name: t("navbar.privilegedUsers"),
        link: "/privileged-cards",
        permission: PERMISSIONS.VIEW_PRIVILEGED_CARDS
      },
      {
        name: t("navbar.newPrivilegedCard"),
        link: "/new-privileged-card",
        permission: PERMISSIONS.VIEW_PRIVILEGED_CARDS
      },
      {
        name: t("navbar.rights"),
        link: "/access",
        permission: PERMISSIONS.VIEW_ACCESS_RIGHTS
      },
      {
        name: t("navbar.resetPassword") || "Reset Password",
        link: "/admin-reset-password",
        permission: PERMISSIONS.ADMIN_RESET_PASSWORD
      },
      {
        name: t("parkingLots.title"),
        link: "/parking-lots",
        permission: PERMISSIONS.MANAGE_PARKING_LOTS
      },
      {
        name: t("navbar.ticketTemplates"),
        link: "/ticket-templates",
        permission: PERMISSIONS.VIEW_TICKET_TEMPLATES
      },
      {
        name: t("navbar.period"),
        link: "/period",
        permission: PERMISSIONS.VIEW_PERIOD
      },
      {
        name: t("navbar.templateManagement") || "Template Management",
        link: "/template-management",
        permission: PERMISSIONS.MANAGE_TEMPLATES
      },
      ...(mode === "1" ? [
        {
          name: t("navbar.templateGroups"),
          link: "/template-groups",
          permission: PERMISSIONS.VIEW_TEMPLATE_GROUPS
        },
        {
          name: t("navbar.templateGroupEvents"),
          link: "/template-group-events",
          permission: PERMISSIONS.VIEW_TEMPLATE_GROUP_EVENTS
        }
      ] : []),
      
      {
        name: t("navbar.logs"),
        link: "/logs",
        permission: PERMISSIONS.VIEW_LOGS
      },
    ]
  };

  useEffect(() => {
    const fetchMode = async () => {
      const res = await getMode();
      setMode(res);
    }
    fetchMode();

    const fetchCameraMode = async () => {
      const res = await getCameraMode();
      setCameraMode(res);
    }
    fetchCameraMode();

    const fetchLatestBackupTime = async () => {
      try {
        const res = await latestBackup();
        console.log(res);

        // Ako latest_backup je null ili nije uspešan zahtev
        if (!res.success || !res.latest_backup) {
          setBackupNeeded("1");
          return;
        }

        const backupDate = new Date(res.latest_backup);
        const today = new Date();

        // Poredimo samo datume (bez vremena)
        const isBackupToday =
          backupDate.getFullYear() === today.getFullYear() &&
          backupDate.getMonth() === today.getMonth() &&
          backupDate.getDate() === today.getDate();

        // Ako backup nije danas, setujemo da je potrebna rezervna kopija
        if (!isBackupToday) {
          setBackupNeeded("1");
        } else {
          setBackupNeeded("0");
        }
      } catch (error) {
        console.error("Greška pri proveri rezervne kopije:", error);
        setBackupNeeded("1"); // U slučaju greške, pretpostavimo da je potrebna kopija
      }
    };
    fetchLatestBackupTime();

    const fetchUpdate = async () => {
      try {
        const res = await getUpdate() ?? res.latest_backup;
        console.log(res);

        // Ako latest_backup je null ili nije uspešan zahtev
        if (res?.success == true) {
          setUpdateFileName(res.update_version);
          setUpdateNeeded("1");
          return;
        }
      } catch (error) {
        console.error("Greška pri proveri verzije:", error);
      }
    };
    fetchUpdate();
  }, [])

  const handleOpenCamera = () => {
    window.location.href = "/hik";
  }
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleBackupClick = async () => {
    setIsLoadingBackup(true);
    try {
      const res = await createBackup();
      console.log("Rezervna kopija uspešno kreirana:", res);
      setBackupNeeded("0");
    } catch (error) {
      console.error("Greška pri kreiranju rezervne kopije:", error);
    } finally {
      setIsLoadingBackup(false);
    }
  };

  const handleUpdateClick = async () => {
    setIsLoadingUpdate(true);
    try {
      const res = await runUpdate(updateFileName);
      console.log("Softver uspešno ažuriran:", res);
      setUpdateNeeded("0");
    } catch (error) {
      console.error("Greška pri ažuriranju softvera:", error);
    } finally {
      setIsLoadingUpdate(false);
    }
  };

  const renderButtonWithMenu = (menuState, setMenuState, menuItems, label) => {
    const filteredItems = menuItems.filter(item => hasPermission(item.permission));
    if (filteredItems.length === 0) return null;

    return (
      <Box sx={{ display: 'flex' }}>
        <Button
          color="inherit"
          sx={{ textTransform: 'none', fontSize: '20px', minHeight: '44px' }}
          onClick={(e) => setMenuState(menuState ? null : e.currentTarget)}
          fullWidth
        >
          {label}
        </Button>

        <Menu
          anchorEl={menuState}
          open={Boolean(menuState)}
          onClose={() => setMenuState(null)}
        >
          {filteredItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={() => setMenuState(null)}
              component="a"
              href={item.href}
              sx={{ color: "text.primary", minHeight: '44px' }}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  };



  return (
    <>
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ height: "64px" }}>
          {/* Glavni Box sa dugmadima */}
          <Box sx={{ display: 'flex' }}>
            {/* Desktop Navbar */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, height: '100%' }}>

              {/* Home */}
              <Button color="inherit" href="/" sx={{ textTransform: 'none', fontSize: '20px', borderBottom: location.pathname === '/' ? '2px solid white' : '2px solid transparent' }}>
                {t('navbar.home')}
              </Button>

              <DesktopMenu menu={AdminMenu} isActive={AdminMenu.subMenu.some(item => item.link === location.pathname)} />
              <DesktopMenu menu={SalesMenu} isActive={SalesMenu.subMenu.some(item => item.link === location.pathname)} />
              {(hasPermission(PERMISSIONS.REFUND) || hasPermission(PERMISSIONS.FISCALIZATION_REPORTS)) && (
              <DesktopMenu menu={FiscalizationMenu} isActive={FiscalizationMenu.subMenu.some(item => item.link === location.pathname)} />
              )}
              {(hasPermission(PERMISSIONS.NEW_EMPLOYEE) ||
                hasPermission(PERMISSIONS.ORGANIZATION_UNITS) ||
                hasPermission(PERMISSIONS.ARTICLE) ||
                hasPermission(PERMISSIONS.UNIT_OF_MEASURE) ||
                hasPermission(PERMISSIONS.INGREDIENTS)) && (

                  <DesktopMenu menu={CanteenMenu} isActive={CanteenMenu.subMenu.some(item => item.link === location.pathname)} />
                )}
              {(hasPermission(PERMISSIONS.NEW_EMPLOYEE) ||
                hasPermission(PERMISSIONS.ORGANIZATION_UNITS) ||
                hasPermission(PERMISSIONS.ARTICLE) ||
                hasPermission(PERMISSIONS.UNIT_OF_MEASURE) ||
                hasPermission(PERMISSIONS.INGREDIENTS)) && (

                  <DesktopMenu menu={LockersMenu} isActive={LockersMenu.subMenu.some(item => item.link === location.pathname)} />
                )}


            </Box>

            {/* Mobile Navbar */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
              <IconButton onClick={handleDrawerToggle} color="inherit">
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Drawer for mobile */}
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={handleDrawerToggle}
              sx={{
                '& .MuiDrawer-paper': {
                  width: 250,
                  padding: '20px',
                },
              }}
            >
              {/* Home */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Button color="inherit" href="/" sx={{ textTransform: 'none', fontSize: '20px', minHeight: '44px' }}>
                  {t('navbar.home')}
                </Button>

                {/* Admin */}
                {renderButtonWithMenu(
                  adminAnchorEl,
                  setAdminAnchorEl,
                  [
                    { label: t('navbar.ticketDesign'), href: '/ticket-design', permission: PERMISSIONS.TICKET_DESIGN },
                    { label: t('navbar.addReader'), href: '/new-reader', permission: PERMISSIONS.CREATE_NEW_READER },
                    { label: t('navbar.edit/deleteReader'), href: '/readers', permission: PERMISSIONS.EDIT_READER },
                    { label: t('navbar.addUser'), href: '/new-user', permission: PERMISSIONS.CREATE_NEW_USER },
                    { label: t('navbar.rights'), href: '/access', permission: PERMISSIONS.VIEW_ACCESS_RIGHTS },
                    { label: t('navbar.resetPassword') || 'Reset Password', href: '/admin-reset-password', permission: PERMISSIONS.ADMIN_RESET_PASSWORD },
                    { label: t('navbar.ticketTemplates'), href: '/ticket-templates', permission: PERMISSIONS.VIEW_TICKET_TEMPLATES },
                    { label: t('navbar.period'), href: '/period', permission: PERMISSIONS.VIEW_PERIOD },
                    { label: t('navbar.templateManagement'), href: '/template-management', permission: PERMISSIONS.MANAGE_TEMPLATES },
                    ...(mode === "1" ? [
                      { label: t('navbar.templateGroups'), href: '/template-groups', permission: PERMISSIONS.VIEW_TEMPLATE_GROUPS },
                      { label: t('navbar.templateGroupEvents'), href: '/template-group-events', permission: PERMISSIONS.VIEW_TEMPLATE_GROUP_EVENTS }
                    ] : []),
                    { label: t('parkingLots.title'), href: '/parking-lots', permission: PERMISSIONS.MANAGE_PARKING_LOTS },
                    { label: t('navbar.privilegedUsers'), href: '/privileged-users', permission: PERMISSIONS.VIEW_PRIVILEGED_CARDS },
                  ],
                  t('navbar.admin')
                )}

                {/* Sales */}
                {renderButtonWithMenu(
                  salesAnchorEl,
                  setSalesAnchorEl,
                  [
                    { label: t('navbar.newPlasticTicket'), href: '/new-ticket', permission: PERMISSIONS.CREATE_NEW_TICKET },
                    { label: t('navbar.newPaperTicket'), href: '/new-paper-ticket', permission: PERMISSIONS.CREATE_NEW_TICKET },
                    { label: t('navbar.addItem'), href: '/new-item', permission: PERMISSIONS.CREATE_NEW_ITEM },
                    { label: t('navbar.edit/deleteItem'), href: '/price-list', permission: PERMISSIONS.VIEW_PRICELIST },
                    { label: t('navbar.addKind'), href: '/add-kind', permission: PERMISSIONS.CREATE_NEW_KIND },
                    { label: t('navbar.addCategory'), href: '/add-category', permission: PERMISSIONS.CREATE_NEW_CATEGORY },
                    { label: t('navbar.addType'), href: '/add-type', permission: PERMISSIONS.CREATE_NEW_TYPE },
                    { label: t('navbar.purchasing'), href: '/purchasing', permission: PERMISSIONS.VIEW_PURCHASING }
                  ],
                  t('navbar.sales')
                )}

                {/* Ormarici */}
                {renderButtonWithMenu(
                  lockersAnchorEl,
                  setLockersAnchorEl,

                  // TODO - promeni dozvole
                  [
                    { label: t('navbar.lockersArangment'), href: '/locker-arangment', permission: PERMISSIONS.NEW_EMPLOYEE },
                    { label: t('navbar.viewLockers'), href: '/lockers', permission: PERMISSIONS.NEW_EMPLOYEE },
                    { label: t('navbar.lockersMonitoring'), href: '/lockers-monitoring', permission: PERMISSIONS.NEW_EMPLOYEE },
                  ],
                  t('navbar.lockers')
                )}
              </Box>
            </Drawer>
          </Box>

          {/* Drugi Box sa ikonama */}
          <Box
            sx={{
              display: "flex",
              position: "fixed", // Koristimo 'fixed' da bude stalno u gornjem desnom uglu
              zIndex: 1000,
              right: 0,
              flexDirection: "row", // Dugmadi vertikalno
              gap: 1, // Razmak između dugmadi
              padding: 1.5, // Dodajte padding ako je potrebno
              justifySelf: "flex-end"
            }}
          >
            {hasPermission(PERMISSIONS.VIEW_MONITORING) && (
              <Tooltip title={t("navbar.tooltip.monitor")}>
                <IconButton sx={{ color: "white" }} href="/monitoring">
                  <MonitorIcon />
                </IconButton>
              </Tooltip>
            )}
            {cameraMode == 1 ? (
              <Tooltip title={t("navbar.tooltip.camera")}>
              <IconButton sx={{ color: "white" }} onClick={() => handleOpenCamera()}>
                <VideocamIcon />
              </IconButton>
              </Tooltip>
            ) : ("")}
            <Tooltip title={t("navbar.tooltip.help")}>
            <IconButton sx={{ color: "white" }} onClick={() => setOpen(true)}>
              <HelpIcon />
            </IconButton>
            </Tooltip>
            <UserManuals open={open} handleClose={() => setOpen(false)} page={location.pathname} />
            <LanguageSwitch />
            <AccountMenu isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
          </Box>

        </Toolbar>
        {backupNeeded == "1" ? (
          <Box
            sx={{
              backgroundColor: "#f47c00",
              height: "25px",
              cursor: isLoadingBackup ? "default" : "pointer", // Onemogući klik dok se učitava
              '&:hover': {
                backgroundColor: isLoadingBackup ? "#f47c00" : "#e67300", // Onemogući hover efekat dok se učitava
              }
            }}
            onClick={isLoadingBackup ? undefined : () => handleBackupClick()} // Onemogući klik dok se učitava
          >
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <InfoRounded sx={{ height: "20px", marginTop: "1px" }} />
              <Typography sx={{ fontSize: "15px", ml: 1 }}>
                {isLoadingBackup ? t("navbar.pleaseWaitBackup") : t("navbar.backupNeeded")}
              </Typography>
              {isLoadingBackup && <CircularProgress size={16} sx={{ color: "white", ml: 1 }} />}
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setBackupNeeded("0"); }}
                sx={{ ml: "auto", color: "white", p: "2px" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : ("")}
        {updateNeeded == "1" ? (
          <Box
            sx={{
              backgroundColor: "#f32f2f",
              height: "25px",
              cursor: isLoadingUpdate ? "default" : "pointer", // Onemogući klik dok se učitava
              '&:hover': {
                backgroundColor: isLoadingUpdate ? "#f32f2f" : "#e01e1e", // Onemogući hover efekat dok se učitava
              }
            }}
            onClick={isLoadingUpdate ? undefined : () => handleUpdateClick()} // Onemogući klik dok se učitava
          >
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <InfoRounded sx={{ height: "20px", marginTop: "1px" }} />
              <Typography sx={{ fontSize: "15px", ml: 1 }}>
                {isLoadingUpdate ? t("navbar.pleaseWaitUpdate") : t("navbar.updateNeeded")}
              </Typography>
              {isLoadingUpdate && <CircularProgress size={16} sx={{ color: "white", ml: 1 }} />}
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setUpdateNeeded("0"); }}
                sx={{ ml: "auto", color: "white", p: "2px" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : ("")}
      </AppBar>

      {/* Spacer to prevent content overlap */}
      <Toolbar />

    </>
  );
};

export default Navbar;
